import React, { useMemo } from "react";
import CustomSelect from "../../globalcontrols/customselect/customselect";
import "./css/hourlyWeekdayHeatmap.css";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const HOURS = Array.from({ length: 24 }, (_, index) => index);

function compactMoney(value) {
  return new Intl.NumberFormat("es-CL", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0));
}

function fullMoney(value) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function HourlyWeekdayHeatmap({
  data,
  error,
  loading,
  locations,
  selectedLocation,
  setSelectedLocation,
  embedded = false,
}) {
  const cells = useMemo(() => {
    const index = new Map();
    (data?.cells || []).forEach((cell) => index.set(`${cell.weekday}-${cell.hour}`, cell));
    return index;
  }, [data]);
  const maxRevenue = Number(data?.max_average_revenue || 0);

  return (
    <section
      className={`${embedded ? "embedded-dashboard-chart" : "dashboard-card"} hourly-heatmap-card`}
      aria-labelledby="hourly-heatmap-title"
    >
      <div className="card-heading hourly-heatmap-heading">
        <div>
          <p className="card-kicker">Patrón semanal</p>
          <h2 id="hourly-heatmap-title">Venta promedio por hora</h2>
          <p>Promedio de ingresos KAKAO en intervalos de una hora, usando horario de Chile.</p>
        </div>
        <div className="hourly-heatmap-location">
          <CustomSelect
            label="Ubicación"
            options={locations}
            selectedValue={selectedLocation}
            onChange={setSelectedLocation}
            labelExtractor={(item) => item.name}
            valueExtractor={(item) => item.id.toString()}
          />
        </div>
      </div>

      {error ? <div className="dashboard-alert" role="alert">{error}</div> : null}
      {loading ? <div className="dashboard-loading" role="status">Calculando promedios…</div> : (
        <div className="hourly-heatmap-scroll">
          <div className="hourly-heatmap-layout">
            <div className="hourly-heatmap-grid" role="img" aria-label="Mapa de calor de venta promedio por día y hora">
              <span className="hourly-heatmap-corner">Hora</span>
              {WEEKDAYS.map((day) => <strong className="hourly-heatmap-day" key={day}>{day}</strong>)}
              {HOURS.flatMap((hour) => {
                const hourLabel = `${String(hour).padStart(2, "0")}:00`;
                return [
                  <strong className="hourly-heatmap-hour" key={`hour-${hour}`}>{hourLabel}</strong>,
                  ...WEEKDAYS.map((day, dayIndex) => {
                    const cell = cells.get(`${dayIndex + 1}-${hour}`) || {
                      average_revenue: 0,
                      average_operations: 0,
                      sampled_days: 0,
                    };
                    const ratio = maxRevenue ? Math.min(1, cell.average_revenue / maxRevenue) : 0;
                    return (
                      <div
                        className="hourly-heatmap-cell"
                        key={`${day}-${hour}`}
                        style={{
                          "--heat": ratio,
                          backgroundColor: `rgba(163, 70, 116, ${0.07 + ratio * 0.88})`,
                          color: ratio > 0.52 ? "#fff" : "#392e34",
                        }}
                        title={`${day} ${hourLabel}–${String(hour + 1).padStart(2, "0")}:00 · ${fullMoney(cell.average_revenue)} promedio · ${cell.average_operations} operaciones promedio`}
                      >
                        {cell.average_revenue ? compactMoney(cell.average_revenue) : "—"}
                      </div>
                    );
                  }),
                ];
              })}
            </div>

            <aside className="hourly-heatmap-scale" aria-label={`Escala de ingreso promedio, desde cero hasta ${fullMoney(maxRevenue)}`}>
              <strong>{fullMoney(maxRevenue)}</strong>
              <span className="hourly-heatmap-gradient" />
              <strong>{fullMoney(0)}</strong>
            </aside>
          </div>
        </div>
      )}
    </section>
  );
}

export default HourlyWeekdayHeatmap;
